<template>
  <div>
    <div class="mb-6">
      <div class="mb-4 flex items-center gap-4">
        <h4 class="font-bold text-md">Monat:</h4>
        <input v-model="selectedMonth" type="month" class="border rounded px-3 py-2" @change="loadTimeAccountData" />
        <button @click="exportDatev" :disabled="datevExporting"
          class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-semibold disabled:opacity-50 admin-button--secondary">
          <i class="fa fa-file-export mr-2"></i>{{ datevExporting ? 'Export wird erstellt...' : 'Datev-Export' }}
        </button>
      </div>
    </div>

    <div v-if="timeAccountData" class="mb-6">
      <h4 class="font-bold text-md mb-3">Zeitkonto-Stand</h4>
      <div v-if="timeAccountStartBalanceChanged" class="bg-red-100 border-l-4 border-red-500 text-red-700 p-3 mb-4">
        <p class="font-bold"><i class="fa fa-exclamation-triangle mr-2"></i>Achtung!</p>
        <p>Änderungen am Startsaldo sollten nur in Ausnahmefällen vorgenommen werden. Dies beeinflusst die
          gesamte Zeitkonto-Berechnung!</p>
      </div>
      <div v-if="successMessage" class="bg-green-100 border-l-4 border-green-500 text-green-700 p-3 mb-4">
        <p class="font-bold"><i class="fa fa-check-circle mr-2"></i>{{ successMessage }}</p>
      </div>
      <div v-if="errorMessage" class="bg-red-100 border-l-4 border-red-500 text-red-700 p-3 mb-4">
        <p class="font-bold"><i class="fa fa-exclamation-triangle mr-2"></i>{{ errorMessage }}</p>
      </div>
      <div v-if="timeAccountData.timeAccount.endBalance !== null"
        class="bg-green-100 border-2 border-green-400 rounded p-3 mb-4">
        <p class="font-bold text-green-800"><i class="fa fa-lock mr-2"></i>Monat geschlossen</p>
        <p class="text-sm text-green-700">Die angezeigten Werte sind der gespeicherte Abrechnungssnapshot.</p>
        <p v-if="timeAccountData.calculations.discrepancyWarning" class="text-sm text-red-700 font-semibold mt-1">
          {{ timeAccountData.calculations.discrepancyWarning }}
        </p>
        <p v-if="timeAccountData.calculations.previousClosingMismatch"
          class="text-sm text-red-700 font-semibold mt-1">
          Erwarteter Startsaldo: {{ Number(timeAccountData.calculations.previousEndBalance).toFixed(2) }}h.
          Neuer Endsaldo bei diesem Startsaldo: {{ Number(timeAccountData.calculations.previewEndBalance).toFixed(2) }}h.
        </p>
        <table v-if="timeAccountData.calculations.discrepancyFields && timeAccountData.calculations.discrepancyFields.length"
          class="mt-2 text-sm w-full">
          <thead>
            <tr class="text-left border-b border-red-200">
              <th class="py-1">Feld</th>
              <th class="py-1">Gespeichert</th>
              <th class="py-1">Aktuell</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="change in timeAccountData.calculations.discrepancyFields" :key="change.key"
              class="border-b border-red-100">
              <td class="py-1">{{ change.label }}</td>
              <td class="py-1">{{ formatDiscrepancy(change) }}</td>
              <td class="py-1">{{ formatDiscrepancy(change, true) }}</td>
            </tr>
          </tbody>
        </table>
        <p v-if="timeAccountData.calculations.snapshotWarning" class="text-sm text-orange-700 font-semibold mt-1">
          {{ timeAccountData.calculations.snapshotWarning }}
        </p>
        <div class="mt-3">
          <label class="block text-sm font-semibold mb-1">Grund für Wiedereröffnung (Pflichtfeld)</label>
          <textarea v-model="reopenReason" rows="2" class="border rounded px-3 py-2 w-full"
            placeholder="Bitte den Grund dokumentieren"></textarea>
          <button @click="reopenTimeAccount()" :disabled="!reopenReason.trim()"
            class="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 font-semibold disabled:opacity-50">
            <i class="fa fa-unlock mr-2"></i>Monat wiedereröffnen
          </button>
          <button v-if="timeAccountData.calculations.discrepancyWarning" @click="refreshTimeAccount()"
            :disabled="!reopenReason.trim() || refreshing"
            class="mt-2 ml-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-semibold disabled:opacity-50">
            <i class="fa fa-refresh mr-2"></i>{{ refreshing ? 'Abrechnung wird aktualisiert...' : 'Abrechnung aktualisieren' }}
          </button>
        </div>
      </div>
      <div v-else class="bg-yellow-100 border-2 border-yellow-400 rounded p-3 mb-4">
        <p class="font-bold text-yellow-800"><i class="fa fa-unlock mr-2"></i>Monat offen</p>
        <p v-if="timeAccountCalculations.pendingChanges" class="text-sm text-yellow-800 mt-1">
          Ungespeicherte Änderungen: Die Vorschau wird nach dem Speichern aktualisiert.
        </p>
      </div>
      <div v-if="user.minijob" class="bg-indigo-50 border-2 border-indigo-300 rounded p-3 mb-4">
        <p class="font-bold text-indigo-900">Minijob-Grenze: 603,00 EUR</p>
        <p class="text-sm text-indigo-800">
          Steuerpflichtig: {{ Number(timeAccountData.timeAccount.taxableGross || 0).toFixed(2) }} EUR
          (inkl. Objektzulagen und Sonderzahlungen).
        </p>
        <p class="text-sm text-indigo-800">
          Steuerfreie Zuschläge: {{ Number(timeAccountData.timeAccount.surchargesAmount || 0).toFixed(2) }} EUR
          (nicht in der Grenze).
        </p>
        <p v-if="timeAccountData.timeAccount.capOverflowHours > 0" class="text-sm text-orange-700 font-semibold">
          {{ Number(timeAccountData.timeAccount.capOverflowHours).toFixed(0) }} volle Stunde(n) wurden in das Zeitkonto verschoben.
        </p>
        <p v-if="timeAccountData.timeAccount.capError" class="text-sm text-red-700 font-semibold">
          {{ timeAccountData.timeAccount.capError }}
        </p>
      </div>

      <div class="grid grid-cols-2 gap-4 mb-4">
        <div class="bg-gray-50 border border-gray-200 rounded p-3">
          <label class="block text-sm font-semibold mb-1">Startsaldo (Stunden)</label>
          <input v-model.number="timeAccountData.timeAccount.startBalance"
            @input="timeAccountStartBalanceChanged = true" type="number" step="0.5"
            class="border rounded px-3 py-2 w-full" :disabled="timeAccountData.timeAccount.endBalance !== null" />
        </div>
        <div class="bg-orange-50 border border-orange-200 rounded p-3">
          <label class="block text-sm font-semibold mb-1">Manuelle Abrechnungskorrektur (Stunden)</label>
          <input v-model.number="timeAccountData.timeAccount.manualCorrection"
            @input="timeAccountManualCorrectionChanged = true" type="number" step="0.5"
            class="border rounded px-3 py-2 w-full" placeholder="z.B. +10 oder -10"
            :disabled="timeAccountData.timeAccount.endBalance !== null" />
          <div class="text-xs text-gray-600 mt-1">+10 = 10h mehr abgerechnet, -10 = 10h mehr ins Zeitkonto
          </div>
        </div>
      </div>

      <div class="grid grid-cols-5 gap-4 mb-4">
        <div class="bg-blue-50 border border-blue-200 rounded p-3">
          <div class="text-sm text-gray-600">Soll-Stunden</div>
          <div class="text-2xl font-bold text-blue-600">{{ timeAccountData.monthlyHours }}h</div>
        </div>
        <div class="bg-green-50 border border-green-200 rounded p-3">
          <div class="text-sm text-gray-600">Gearbeitete Stunden</div>
          <div class="text-2xl font-bold text-green-600">{{ timeAccountCalculations.workedHours }}h</div>
        </div>
        <div class="bg-orange-50 border border-orange-200 rounded p-3">
          <div class="text-sm text-gray-600">Geplante Stunden</div>
          <div class="text-2xl font-bold text-orange-600">{{ timeAccountCalculations.plannedHours }}h</div>
        </div>
        <div class="bg-purple-50 border border-purple-200 rounded p-3">
          <div class="text-sm text-gray-600">Endsaldo</div>
          <div class="text-2xl font-bold text-purple-600">{{ timeAccountCalculations.endBalance }}h</div>
        </div>
        <div class="bg-yellow-50 border border-yellow-200 rounded p-3">
          <div class="text-sm text-gray-600">Abrechnungskorrektur</div>
          <div class="text-lg font-bold"
            :class="timeAccountCalculations.totalCorrection >= 0 ? 'text-yellow-600' : 'text-yellow-800'">
            {{ timeAccountCalculations.totalCorrection > 0 ? '+' : '' }}{{
              timeAccountCalculations.totalCorrection.toFixed(2) }}h
          </div>
          <div class="text-xs text-gray-600 mt-1">
            Manuell: {{ timeAccountCalculations.manualCorrection > 0 ? '+' : '' }}{{
              timeAccountCalculations.manualCorrection.toFixed(2) }}h<br>
            Auto: {{ timeAccountCalculations.autoCorrection > 0 ? '+' : '' }}{{
              timeAccountCalculations.autoCorrection.toFixed(2) }}h
          </div>
        </div>
      </div>

      <div class="flex gap-3">
        <button v-if="timeAccountStartBalanceChanged" @click="saveTimeAccountStartBalance"
          class="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 font-semibold mb-4 admin-button--primary">
          <i class="fa fa-save mr-2"></i>Startsaldo speichern
        </button>
        <button v-if="timeAccountManualCorrectionChanged" @click="saveTimeAccountStartBalance"
          class="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 font-semibold mb-4 admin-button--primary">
          <i class="fa fa-save mr-2"></i>Manuelle Korrektur speichern
        </button>
      </div>
    </div>

    <div v-if="timeAccountData" class="mb-6">
      <h4 class="font-bold text-md mb-3">Berechnung</h4>
      <div class="bg-gray-50 border border-gray-200 rounded p-4 mb-4">
        <div class="grid grid-cols-2 gap-4 mb-3">
          <div>
            <span class="font-semibold">Abgerechnete Stunden:</span>
            <span class="ml-2">{{ timeAccountCalculations.billedHours }}h</span>
          </div>
          <div>
            <span class="font-semibold">Differenz:</span>
            <span class="ml-2" :class="timeAccountCalculations.difference >= 0 ? 'text-green-600' : 'text-red-600'">
              {{ timeAccountCalculations.difference > 0 ? '+' : '' }}{{ timeAccountCalculations.difference
              }}h
            </span>
          </div>
        </div>
        <div v-if="timeAccountCalculations.warning" class="text-sm text-red-600 font-semibold mt-2">
          <i class="fa fa-exclamation-triangle mr-1"></i>{{ timeAccountCalculations.warning }}
        </div>
      </div>

      <button v-if="timeAccountData.timeAccount.endBalance === null" @click="calculateTimeAccount()"
        :disabled="timeAccountCalculations.pendingChanges"
        class="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 font-semibold disabled:opacity-50 admin-button--primary">
        <i class="fa fa-calculator mr-2"></i>{{ timeAccountCalculations.pendingChanges ? 'Erst speichern, dann abrechnen' : 'Abrechnen' }}
      </button>
    </div>

    <div v-if="timeAccountData && timeAccountData.hoursByRubrik" class="mb-6">
      <div class="flex justify-between items-center mb-3">
        <h4 class="font-bold text-md">Stunden nach Rubrikencode mit Berechnung</h4>
        <div class="flex items-center">
          <input id="showWorkedHoursOnly" type="checkbox" v-model="showWorkedHoursOnly"
            class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500" />
          <label for="showWorkedHoursOnly" class="text-sm font-medium text-gray-700 mr-2">Nur gearbeitete
            Stunden</label>
        </div>
      </div>
      <div class="bg-white border-2 border-gray-300 rounded-lg overflow-hidden mb-4">
        <table class="w-full">
          <thead class="bg-gray-100 border-b-2 border-gray-300">
            <tr>
              <th class="py-3 px-4 text-left text-sm font-semibold text-gray-700">Rubrikencode</th>
              <th class="py-3 px-4 text-right text-sm font-semibold text-gray-700">Stunden</th>
              <th class="py-3 px-4 text-right text-sm font-semibold text-gray-700">Preis/Std.</th>
              <th class="py-3 px-4 text-right text-sm font-semibold text-gray-700">Betrag</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(data, rubrik) in timeAccountData.hoursByRubrik" :key="rubrik" :class="[
              'border-b border-gray-200',
              data.isSurcharge ? 'bg-blue-50' : 'hover:bg-gray-50'
            ]">
              <td class="py-3 px-4 text-sm" :class="{ 'pl-12 italic text-gray-600': data.isSurcharge }">
                {{ rubrik }}
              </td>
              <td class="py-3 px-4 text-right text-sm"
                :class="[data.isSurcharge ? 'italic text-gray-600' : 'font-semibold']">
                {{ (showWorkedHoursOnly ? (data.actualHours || 0) : (data.plannedHours || 0)).toFixed(2) }}h
              </td>
              <td class="py-3 px-4 text-right text-sm" :class="{ 'italic text-gray-600': data.isSurcharge }">
                {{ data.baseRate ? data.baseRate.toFixed(2) + ' €' : '-' }}
              </td>
              <td class="py-3 px-4 text-right text-sm"
                :class="[data.isSurcharge ? 'font-semibold italic text-gray-600' : 'font-bold text-green-800']">
                {{ (showWorkedHoursOnly ? (data.actualAmount || 0) : (data.plannedAmount || 0)).toFixed(2)
                }} €
              </td>
            </tr>
          </tbody>
          <tfoot class="bg-gray-100 border-t-2 border-gray-300">
            <tr>
              <td class="py-3 px-4 text-sm font-bold">Gesamt</td>
              <td class="py-3 px-4 text-right text-sm font-bold">
                {{ timeAccountTableTotals.hours.toFixed(2) }}h
              </td>
              <td class="py-3 px-4"></td>
              <td class="py-3 px-4 text-right">
                <span class="text-right text-lg font-bold text-green-800">
                  {{ timeAccountTableTotals.amount.toFixed(2) }} €
                </span><br>
                <span class="text-right !text-sm font-bold text-green-800"
                  v-if="showWorkedHoursOnly && Math.abs(timeAccountTableTotals.correctionHours) > 0.001">
                  Davon Zeitkonto-Korrektur: {{ timeAccountTableTotals.correctionHours.toFixed(2) }}h /
                  {{ timeAccountTableTotals.correctionAmount.toFixed(2) }} €
                </span><br v-if="showWorkedHoursOnly && Math.abs(timeAccountTableTotals.correctionHours) > 0.001">
                <span class="text-right !text-sm font-bold text-green-800" v-if="showWorkedHoursOnly">
                  Davon Zuschläge: {{ timeAccountData.timeAccount.surchargesAmount.toFixed(2) }} €
                </span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <div v-if="timeAccountData && timeAccountData.jobs" class="mb-6">
      <h4 class="font-bold text-md mb-3">Jobs des Monats</h4>
      <div v-if="timeAccountData.jobs.length" class="overflow-y-auto max-h-96">
        <table class="w-full border">
          <thead class="bg-caspgrey">
            <tr>
              <th class="py-2 px-3 text-left">Datum</th>
              <th class="py-2 px-3 text-left">Einsatzstelle</th>
              <th class="py-2 px-3 text-left">Beschreibung</th>
              <th class="py-2 px-3 text-right">Soll</th>
              <th class="py-2 px-3 text-right">Ist</th>
              <th class="py-2 px-3 text-right">Differenz</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="job in timeAccountData.jobs" :key="job.id" class="border-b hover:bg-gray-50">
              <td class="py-2 px-3 text-sm">{{ job.Datum_von }}</td>
              <td class="py-2 px-3 text-sm">{{ job.Einsatzstelle_Beschreibung }}</td>
              <td class="py-2 px-3 text-sm">{{ job.Vertragsbeschreibung }}</td>
              <td class="py-2 px-3 text-right text-sm">{{ getJobHours(job, false).toFixed(2) }}h</td>
              <td class="py-2 px-3 text-right text-sm">{{ getJobHours(job, true).toFixed(2) }}h</td>
              <td class="py-2 px-3 text-right text-sm" :class="{
                'text-green-600': getJobHours(job, true) >= getJobHours(job, false),
                'text-red-600': getJobHours(job, true) < getJobHours(job, false)
              }">
                {{ (getJobHours(job, true) - getJobHours(job, false)).toFixed(2) }}h
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="text-gray-500 italic">
        Keine Jobs für diesen Monat gefunden.
      </div>
    </div>
  </div>
</template>

<script>
import CASP from "@/CASP.js";
import { calculateTimeAccountTableTotals } from "@/components/timeAccountTableTotals";

export default {
  name: 'TimeAccountTab',
  props: {
    user: {
      type: Object,
      required: true
    },
    month: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      selectedMonth: this.month || new Date().toISOString().slice(0, 7), // Use passed month or current month as fallback
      timeAccountData: null,
      timeAccountStartBalanceChanged: false,
      timeAccountManualCorrectionChanged: false,
      showWorkedHoursOnly: true,
      manualCorrectionAmount: 0,
      successMessage: '',
      errorMessage: '',
      reopenReason: '',
      refreshing: false,
      savedStartBalance: null,
      savedManualCorrection: null,
      datevExporting: false
    };
  },
  computed: {
    timeAccountCalculations() {
      const calc = this.timeAccountData.calculations;
      const manualCorrection = Number(this.timeAccountData.timeAccount.manualCorrection || 0);
      const autoCorrection = Number(calc.autoCorrection || 0);
      const totalCorrection = manualCorrection + autoCorrection;
      const totalCorrectionAmount = totalCorrection * Number(
        calc.regularBaseRate || this.timeAccountData.baseTariff.categoryPrices.ZEITKONTO || 0
      );
      return {
        endBalance: Number(calc.endBalance || 0).toFixed(2),
        billedHours: Number(calc.billedHours || 0).toFixed(2),
        plannedHours: Number(calc.plannedHours || 0).toFixed(2),
        difference: (Number(calc.workedHours || 0) - Number(this.timeAccountData.monthlyHours || 0)).toFixed(2),
        workedHours: Number(calc.workedHours || 0).toFixed(2),
        manualCorrection,
        autoCorrection,
        totalCorrection: totalCorrection,
        totalCorrectionAmount: totalCorrectionAmount,
        pendingChanges: this.savedStartBalance !== null && (
          Number(this.timeAccountData.timeAccount.startBalance) !== Number(this.savedStartBalance) ||
          Number(this.timeAccountData.timeAccount.manualCorrection) !== Number(this.savedManualCorrection)
        ),
        warning: Number(calc.endBalance || 0) < 0 ? "Es fehlen noch " + (Number(calc.endBalance) * -1).toFixed(2) + " Stunden" : ""
      };
    },
    timeAccountTableTotals() {
      const calculation = this.timeAccountData.calculations;
      return calculateTimeAccountTableTotals({
        showWorkedHoursOnly: this.showWorkedHoursOnly,
        plannedHours: calculation.plannedHours,
        workedHours: calculation.workedHours,
        autoCorrection: calculation.autoCorrection,
        manualCorrection: this.timeAccountData.timeAccount.manualCorrection,
        regularBaseRate: calculation.regularBaseRate,
        totalAmount: this.timeAccountData.totalAmount,
        hoursByRubrik: this.timeAccountData.hoursByRubrik
      });
    }
  },
  methods: {
    getJobHours(job, actual) {
      const calculatedHours = actual ? job.actualHours : job.plannedHours;
      if (calculatedHours !== undefined && calculatedHours !== null) {
        return Number(calculatedHours) || 0;
      }

      const startTime = actual ? job.Von_Uhrzeit : job.Soll_Von;
      const endTime = actual ? job.Bis_Uhrzeit : job.Soll_Bis;
      if (!startTime || !endTime) return 0;

      const toMinutes = time => {
        const [hours, minutes] = String(time).split(':').map(Number);
        if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
        return (hours * 60) + minutes;
      };

      const startMinutes = toMinutes(startTime);
      const endMinutes = toMinutes(endTime);
      if (startMinutes === null || endMinutes === null) return 0;

      let durationMinutes = endMinutes - startMinutes;
      if (durationMinutes < 0) durationMinutes += 24 * 60;

      return durationMinutes / 60;
    },
    calculateTotalPlannedAmount() {
      if (!this.timeAccountData || !this.timeAccountData.hoursByRubrik) return 0;

      let total = 0;
      Object.values(this.timeAccountData.hoursByRubrik).forEach(data => {
        total += data.plannedAmount || 0;
      });

      return total;
    },
    loadTimeAccountData() {
      document.getElementById("mainspinner").style.display = "inline-block";
      CASP.getTimeAccount(this.user.id, this.selectedMonth, (status, response) => {
        document.getElementById("mainspinner").style.display = "none";
        if (status === 200) {
          this.timeAccountData = response;
          this.savedStartBalance = response.timeAccount.startBalance;
          this.savedManualCorrection = response.timeAccount.manualCorrection;
          this.timeAccountStartBalanceChanged = false;
          this.errorMessage = '';
        } else {
          this.showErrorMessage(response);
        }
      });
    },
    saveTimeAccountStartBalance() {
      if ((!this.timeAccountStartBalanceChanged && !this.timeAccountManualCorrectionChanged) || !this.timeAccountData) return;

      document.getElementById("mainspinner").style.display = "inline-block";
      CASP.updateTimeAccountStartBalance(
        this.user.id,
        this.selectedMonth,
        this.timeAccountData.timeAccount.startBalance,
        this.timeAccountData.timeAccount.manualCorrection,
        (status, response) => {
          document.getElementById("mainspinner").style.display = "none";
          if (status === 200) {
            this.timeAccountStartBalanceChanged = false;
            this.timeAccountManualCorrectionChanged = false;
            this.showSuccessMessage('Zeitkonto-Daten erfolgreich gespeichert');
            this.$emit('changed');
            this.loadTimeAccountData();
          } else {
            this.showErrorMessage(response);
          }
        }
      );
    },
    calculateTimeAccount(confirmed = false) {
      if (!this.timeAccountData) return;
      const warnings = this.timeAccountData.exportWarnings;
      if (warnings && warnings.hasExports && !confirmed && !window.confirm(
        `Für diesen und/oder folgende Monate existieren DATEV-Dokumente (${this.formatExportWarnings(warnings)}). Die Dateien werden nicht neu erstellt. Trotzdem abrechnen?`
      )) return;
      confirmed = confirmed || Boolean(warnings && warnings.hasExports);

      document.getElementById("mainspinner").style.display = "inline-block";
      CASP.calculateTimeAccount(this.user.id, this.selectedMonth, confirmed, (status, response) => {
        document.getElementById("mainspinner").style.display = "none";
        if (status === 200) {
          this.showSuccessMessage(response && response.warning
            ? `Zeitkonto erfolgreich abgerechnet. ${response.warning}`
            : 'Zeitkonto erfolgreich abgerechnet');
          this.$emit('changed');
          this.loadTimeAccountData();
        } else {
          if (response && response.requiresConfirmation && window.confirm(response.error)) {
            this.calculateTimeAccount(true);
            return;
          }
          this.showErrorMessage(response);
        }
      });
    },
    reopenTimeAccount(confirmed = false) {
      if (!this.reopenReason.trim() || !this.timeAccountData) return;
      const warnings = this.timeAccountData.exportWarnings;
      if (warnings && warnings.hasExports && !confirmed) {
        confirmed = window.confirm(
          `Für diesen und/oder folgende Monate existieren DATEV-Dokumente (${this.formatExportWarnings(warnings)}). Die Dateien werden nicht neu erstellt. Trotzdem wiedereröffnen?`
        );
        if (!confirmed) return;
      }
      CASP.reopenTimeAccount(this.user.id, this.selectedMonth, this.reopenReason.trim(), confirmed, (status, response) => {
        if (status === 200) {
          this.reopenReason = '';
          this.showSuccessMessage(response && response.warning
            ? `Zeitkonto wiedereröffnet. ${response.warning}`
            : 'Zeitkonto wiedereröffnet. Bitte nach der Korrektur erneut abrechnen.');
          this.$emit('changed');
          this.loadTimeAccountData();
        } else {
          const parsed = this.getApiResponse(response);
          if (parsed.requiresConfirmation && window.confirm(parsed.error)) {
            this.reopenTimeAccount(true);
            return;
          }
          this.showErrorMessage(parsed);
        }
      });
    },
    refreshTimeAccount(confirmed = false) {
      if (!this.reopenReason.trim() || !this.timeAccountData || this.refreshing) return;
      const warnings = this.timeAccountData.exportWarnings;
      if (warnings && warnings.hasExports && !confirmed) {
        confirmed = window.confirm(
          `Für diesen und/oder folgende Monate existieren DATEV-Dokumente (${this.formatExportWarnings(warnings)}). Die Dateien werden nicht neu erstellt. Trotzdem aktualisieren?`
        );
        if (!confirmed) return;
      }
      this.refreshing = true;
      CASP.refreshTimeAccount(
        this.user.id,
        this.selectedMonth,
        this.reopenReason.trim(),
        confirmed,
        this.timeAccountData.calculations.expectedUpdatedAt,
        (status, response) => {
          this.refreshing = false;
          if (status === 200) {
            this.reopenReason = '';
            const affected = response.affectedMonths && response.affectedMonths.length
              ? ` Betroffene Monate: ${response.affectedMonths.join(', ')}.`
              : '';
            this.showSuccessMessage(`Abrechnung aktualisiert.${affected}`);
            this.$emit('changed');
            this.loadTimeAccountData();
          } else {
            const parsed = this.getApiResponse(response);
            if (parsed.requiresConfirmation && window.confirm(parsed.error)) {
              this.refreshTimeAccount(true);
              return;
            }
            this.showErrorMessage(parsed);
          }
        }
      );
    },
    formatDiscrepancy(change, next = false) {
      const value = next ? change.next : change.old;
      return `${Number(value).toFixed(2)} ${change.unit === 'EUR' ? 'EUR' : 'h'}`;
    },
    exportDatev() {
      if (this.datevExporting) return;

      this.datevExporting = true;
      document.getElementById("mainspinner").style.display = "inline-block";
      CASP.exportDatevForUser(this.user.id, this.selectedMonth, (status, response) => {
        document.getElementById("mainspinner").style.display = "none";
        this.datevExporting = false;

        if (status === 200 && response && response.lodasOtl && response.readableOtl) {
          const apiUrl = process.env.VUE_APP_API_URL;
          window.open(apiUrl + '/assets/' + response.lodasOtl, '_blank');
          window.open(apiUrl + '/assets/' + response.readableOtl, '_blank');
          const warningText = response.warnings && response.warnings.length
            ? ` Export enthält ${response.warnings.length} Hinweis(e).`
            : '';
          this.showSuccessMessage('DATEV-Export erstellt.' + warningText);
          this.loadTimeAccountData();
        } else {
          const message = this.getApiResponse(response);
          this.showErrorMessage(message);
        }
      });
    },
    getApiResponse(response) {
      if (typeof response === 'string') {
        try {
          return JSON.parse(response);
        } catch (error) {
          return { error: response };
        }
      }
      return response || {};
    },
    formatExportWarnings(warnings) {
      const tracked = (warnings.months || []).join(', ');
      const legacy = (warnings.legacyAmbiguousMonths || []).map(month =>
        `${month} historisch, Mitarbeiter unbekannt`
      ).join(', ');
      return [tracked, legacy].filter(Boolean).join(', ');
    },
    showSuccessMessage(message) {
      this.errorMessage = '';
      this.successMessage = message;
      setTimeout(() => {
        this.successMessage = '';
      }, 3000);
    },
    showErrorMessage(response) {
      const parsed = this.getApiResponse(response);
      this.successMessage = '';
      this.errorMessage = parsed && (parsed.error || parsed.desc)
        ? (parsed.error || parsed.desc)
        : 'Die Zeitkonto-Aktion konnte nicht ausgeführt werden.';
    }
  },
  watch: {
    month(newMonth) {
      if (newMonth && newMonth !== this.selectedMonth) {
        this.selectedMonth = newMonth;
        this.loadTimeAccountData();
      }
    }
  },
  mounted() {
    this.loadTimeAccountData();
  }
};
</script>
